
import { Component, Prop, Vue } from "nuxt-property-decorator";

import arrowLeftSrc from "~/assets/icons/arrow-left-solid.svg";
import arrowRightSrc from "~/assets/icons/arrow-right-solid.svg";

export type CarouselItem = Record<string, unknown>;

const CARD_GAP = 24;
const SCROLL_STEP_CARDS = 2;

let carouselIdCounter = 0;

@Component
export default class CardCarousel extends Vue {
  @Prop({ required: true }) title!: string;
  @Prop({ required: true }) footerLinkText!: string;
  @Prop({ required: true }) footerLinkUrl!: string;
  @Prop({ required: true }) items!: CarouselItem[];
  @Prop({ default: null }) itemKey!: string | null;

  isAtStart = true;
  isAtEnd = false;
  arrowLeftIcon = arrowLeftSrc;
  arrowRightIcon = arrowRightSrc;
  private resizeObserver: ResizeObserver | null = null;
  private scrollRafId: number | null = null;
  private instanceId = carouselIdCounter++;

  get carouselTrackId(): string {
    return `card-carousel-track-${this.instanceId}`;
  }

  private get trackWrapperEl(): HTMLElement | null {
    return (this.$refs.trackWrapper as HTMLElement) ?? null;
  }

  mounted() {
    const wrapper = this.trackWrapperEl;
    if (wrapper) {
      wrapper.addEventListener("scroll", this.updateScrollState, {
        passive: true,
      });
      this.resizeObserver = new ResizeObserver(this.updateScrollState);
      this.resizeObserver.observe(wrapper);
      this.updateScrollState();
    }
  }

  beforeDestroy() {
    const wrapper = this.trackWrapperEl;
    if (wrapper) {
      wrapper.removeEventListener("scroll", this.updateScrollState);
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.scrollRafId) {
      cancelAnimationFrame(this.scrollRafId);
      this.scrollRafId = null;
    }
  }

  updateScrollState() {
    if (this.scrollRafId) return;
    this.scrollRafId = requestAnimationFrame(() => {
      this.scrollRafId = null;
      const wrapper = this.trackWrapperEl;
      if (!wrapper) return;
      this.isAtStart = wrapper.scrollLeft <= 0;
      this.isAtEnd =
        wrapper.scrollLeft + wrapper.clientWidth >= wrapper.scrollWidth - 1;
    });
  }

  scrollLeft() {
    const wrapper = this.trackWrapperEl;
    if (!wrapper) return;
    const card = wrapper.querySelector<HTMLElement>(".card-carousel__card");
    const step = (card?.offsetWidth ?? 269) + CARD_GAP;
    wrapper.scrollBy({
      left: -step * SCROLL_STEP_CARDS,
      behavior: "smooth",
    });
  }

  scrollRight() {
    const wrapper = this.trackWrapperEl;
    if (!wrapper) return;
    const card = wrapper.querySelector<HTMLElement>(".card-carousel__card");
    const step = (card?.offsetWidth ?? 269) + CARD_GAP;
    wrapper.scrollBy({
      left: step * SCROLL_STEP_CARDS,
      behavior: "smooth",
    });
  }
}
